import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import staffService from '../services/staffService';
import { loadBusinessSettings } from '../utils/businessSettingsHelper';
import './StaffCards.css';

const StaffCards = () => {
  const [staff, setStaff] = useState([]);
  const [businessSettings, setBusinessSettings] = useState({
    monthly_profit_target: 0,
    total_annual_overhead: 0,
    weeks_per_year: 48,
    staff_utilization_rate: 85
  });
  
  const [newStaffMember, setNewStaffMember] = useState({
    name: '',
    available_hours: 0,
    booked_hours: 0,
    is_revenue_generating: true,
    hourly_pay: 0,
    photo_url: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  
  useEffect(() => {
    fetchBusinessSettings();
    fetchStaff();
  }, []);
  
  const fetchBusinessSettings = async () => {
    try {
      const settings = await loadBusinessSettings();
      if (settings) {
        setBusinessSettings({
          monthly_profit_target: parseFloat(settings.monthly_profit_target) || 0,
          total_annual_overhead: parseFloat(settings.total_annual_overhead) || 0,
          weeks_per_year: parseInt(settings.weeks_per_year) || 48,
          staff_utilization_rate: parseFloat(settings.staff_utilization_rate) || 85
        });
      }
    } catch (error) {
      console.error('Error fetching business settings:', error);
      toast.error('Failed to load business settings');
    }
  };
  
  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await staffService.getStaff();
      setStaff(response || []);
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error('Failed to load staff data');
      setStaff([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate capacity percentage for individual staff
  const calculateCapacity = (staffMember) => {
    if (!staffMember.available_hours || staffMember.available_hours <= 0) return 0;
    return (staffMember.booked_hours / staffMember.available_hours) * 100;
  };
  
  // Handle staff input changes
  const handleStaffChange = (id, field, value) => {
    const updatedStaff = staff.map(member => {
      if (member.id === id) {
        const updated = { ...member, [field]: parseFloat(value) || 0 };
        
        // If changing available_hours, ensure booked_hours doesn't exceed it
        if (field === 'available_hours' && updated.booked_hours > value) {
          updated.booked_hours = parseFloat(value);
        }
        
        return updated;
      }
      return member;
    });
    setStaff(updatedStaff);
  };
  
  // Handle staff type change
  const handleStaffTypeChange = (id, isRevenueGenerating) => {
    const updatedStaff = staff.map(member => {
      if (member.id === id) {
        return { ...member, staff_type: isRevenueGenerating ? 'revenue' : 'support' };
      }
      return member;
    });
    setStaff(updatedStaff);
  };
  
  // Add new staff member
  const handleAddStaff = async () => {
    if (!newStaffMember.name.trim()) {
      toast.error('Please enter a staff name');
      return;
    }
    
    const staffData = {
      name: newStaffMember.name,
      staff_type: newStaffMember.is_revenue_generating ? 'revenue' : 'support',
      hourly_pay: parseFloat(newStaffMember.hourly_pay) || 0,
      available_hours: parseFloat(newStaffMember.available_hours) || 0,
      booked_hours: parseFloat(newStaffMember.booked_hours) || 0,
      photo_url: newStaffMember.photo_url || '',
      commission_rate: 0
    };
    
    try {
      const response = await staffService.createStaffMember(staffData);
      const newStaffWithId = { ...staffData, id: response.id };
      
      setStaff([...staff, newStaffWithId]);
      toast.success(`${newStaffMember.name} added to your team!`);
      
      // Reset form
      setNewStaffMember({
        name: '',
        available_hours: 0,
        booked_hours: 0,
        is_revenue_generating: true,
        hourly_pay: 0,
        photo_url: ''
      });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error creating staff member:', error);
      toast.error('Failed to add staff member');
    }
  };
  
  // Delete staff member
  const handleDeleteStaff = async (staffMember) => {
    const confirmDelete = window.confirm(`Remove ${staffMember.name} from your team?`);
    
    if (confirmDelete) {
      try {
        if (staffMember.id) {
          await staffService.deleteStaffMember(staffMember.id);
        }
        setStaff(staff.filter(member => member.id !== staffMember.id));
        toast.success(`${staffMember.name} removed from your team`);
      } catch (error) {
        console.error('Error deleting staff member:', error);
        toast.error('Failed to remove staff member');
      }
    }
  };
  
  // Save all staff changes
  const handleSaveAllStaff = async () => {
    try {
      await staffService.updateStaff(staff);
      toast.success('Staff data saved successfully!');
      fetchStaff();
    } catch (error) {
      console.error('Error saving staff data:', error);
      toast.error('Failed to save staff data');
    }
  };
  
  // Filter staff based on active tab
  const getFilteredStaff = () => {
    if (activeTab === 'revenue') {
      return staff.filter(member => member.staff_type === 'revenue');
    } else if (activeTab === 'support') {
      return staff.filter(member => member.staff_type === 'support');
    }
    return staff;
  };
  
  // Calculate team statistics
  const calculateTeamStats = () => {
    const revenueStaff = staff.filter(member => member.staff_type === 'revenue');
    const supportStaff = staff.filter(member => member.staff_type === 'support');
    
    const totalRevenueAvailableHours = revenueStaff.reduce((sum, member) => 
      sum + parseFloat(member.available_hours || 0), 0);
    
    const totalRevenueBookedHours = revenueStaff.reduce((sum, member) => 
      sum + parseFloat(member.booked_hours || 0), 0);
    
    const avgRevenueCapacity = totalRevenueAvailableHours > 0 
      ? (totalRevenueBookedHours / totalRevenueAvailableHours) * 100 
      : 0;
    
    const supportStaffWeeklyCost = supportStaff.reduce((sum, member) => 
      sum + (parseFloat(member.hourly_pay || 0) * parseFloat(member.available_hours || 0)), 0);
    
    const supportStaffAnnualCost = supportStaffWeeklyCost * businessSettings.weeks_per_year;
    
    // Calculate overhead per minute
    const monthlyProfit = parseFloat(businessSettings.monthly_profit_target) || 0;
    const annualProfit = monthlyProfit * 12;
    const annualOverhead = parseFloat(businessSettings.total_annual_overhead) || 0;
    
    // Use business settings utilization rate
    const utilizedHours = totalRevenueAvailableHours * (businessSettings.staff_utilization_rate / 100);
    const annualRevenueMinutes = utilizedHours * businessSettings.weeks_per_year * 60;
    
    const overheadPerMinute = annualRevenueMinutes > 0 
      ? ((annualOverhead + annualProfit) / annualRevenueMinutes)
      : 0;
    
    const overheadPerHour = overheadPerMinute * 60;
    
    return {
      revenueStaffCount: revenueStaff.length,
      supportStaffCount: supportStaff.length,
      totalRevenueAvailableHours,
      totalRevenueBookedHours,
      avgRevenueCapacity,
      supportStaffWeeklyCost,
      supportStaffAnnualCost,
      overheadPerMinute,
      overheadPerHour,
      annualRevenueMinutes
    };
  };
  
  const teamStats = calculateTeamStats();
  const filteredStaff = getFilteredStaff();
  
  if (loading) {
    return (
      <div className="staff-cards-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading your team...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="staff-cards-container">
      {/* Header */}
      <div className="staff-header">
        <h1>👥 Your Team</h1>
        <p className="staff-subtitle">
          Manage your staff to optimize service pricing and track performance
        </p>
        
        {/* Team Summary */}
        <div className="team-summary">
          <div className="summary-card">
            <div className="summary-icon">💼</div>
            <div className="summary-content">
              <div className="summary-number">{teamStats.revenueStaffCount}</div>
              <div className="summary-label">Revenue Staff</div>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">🛠️</div>
            <div className="summary-content">
              <div className="summary-number">{teamStats.supportStaffCount}</div>
              <div className="summary-label">Support Staff</div>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">⏰</div>
            <div className="summary-content">
              <div className="summary-number">{teamStats.totalRevenueBookedHours.toFixed(0)}</div>
              <div className="summary-label">Weekly Hours</div>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📈</div>
            <div className="summary-content">
              <div className="summary-number">{teamStats.avgRevenueCapacity.toFixed(0)}%</div>
              <div className="summary-label">Capacity</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="staff-tabs">
        <button 
          className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All Team ({staff.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'revenue' ? 'active' : ''}`}
          onClick={() => setActiveTab('revenue')}
        >
          Revenue Staff ({teamStats.revenueStaffCount})
        </button>
        <button 
          className={`tab-button ${activeTab === 'support' ? 'active' : ''}`}
          onClick={() => setActiveTab('support')}
        >
          Support Staff ({teamStats.supportStaffCount})
        </button>
      </div>
      
      {/* Add Staff Section */}
      <div className="add-staff-section">
        {!showAddForm ? (
          <button 
            className="add-staff-button"
            onClick={() => setShowAddForm(true)}
          >
            <span className="add-icon">+</span>
            Add Team Member
          </button>
        ) : (
          <div className="add-staff-form">
            <h3>Add New Team Member</h3>
            <div className="form-grid">
              <input
                type="text"
                placeholder="Staff name (e.g., Sarah)"
                value={newStaffMember.name}
                onChange={(e) => setNewStaffMember({...newStaffMember, name: e.target.value})}
                className="form-input"
              />
              <select 
                value={newStaffMember.is_revenue_generating.toString()}
                onChange={(e) => setNewStaffMember({
                  ...newStaffMember, 
                  is_revenue_generating: e.target.value === "true"
                })}
                className="form-select"
              >
                <option value="true">Revenue Generating</option>
                <option value="false">Support Staff</option>
              </select>
              <input
                type="number"
                placeholder="Hourly pay (£)"
                value={newStaffMember.hourly_pay || ''}
                onChange={(e) => setNewStaffMember({...newStaffMember, hourly_pay: e.target.value})}
                className="form-input"
                step="0.1"
                min="0"
              />
              <input
                type="number"
                placeholder="Available hours/week"
                value={newStaffMember.available_hours || ''}
                onChange={(e) => setNewStaffMember({...newStaffMember, available_hours: e.target.value})}
                className="form-input"
                step="0.1"
                min="0"
              />
              <input
                type="url"
                placeholder="Photo URL (optional)"
                value={newStaffMember.photo_url}
                onChange={(e) => setNewStaffMember({...newStaffMember, photo_url: e.target.value})}
                className="form-input"
              />
            </div>
            <div className="form-actions">
              <button className="btn-primary" onClick={handleAddStaff}>
                Add to Team
              </button>
              <button 
                className="btn-secondary" 
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Staff Cards Grid */}
      <div className="staff-cards-grid">
        {filteredStaff.map((member) => (
          <div 
            key={member.id} 
            className={`staff-card ${member.staff_type === 'revenue' ? 'revenue-staff' : 'support-staff'}`}
          >
            {/* Staff Photo */}
            <div className="staff-photo">
              {member.photo_url ? (
                <img src={member.photo_url} alt={member.name} />
              ) : (
                <div className="default-avatar">
                  {member.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="staff-type-badge">
                {member.staff_type === 'revenue' ? '💼' : '🛠️'}
              </div>
            </div>
            
            {/* Staff Info */}
            <div className="staff-info">
              <h3 className="staff-name">{member.name}</h3>
              <p className="staff-role">
                {member.staff_type === 'revenue' ? 'Revenue Generating' : 'Support Staff'}
              </p>
              
              {/* Staff Metrics */}
              <div className="staff-metrics">
                {member.staff_type === 'revenue' && (
                  <div className="metric">
                    <div className="metric-label">Capacity</div>
                    <div className="metric-value">
                      {calculateCapacity(member).toFixed(0)}%
                    </div>
                  </div>
                )}
                <div className="metric">
                  <div className="metric-label">Weekly Cost</div>
                  <div className="metric-value">
                    £{((member.hourly_pay || 0) * (member.available_hours || 0)).toFixed(0)}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Staff Controls */}
            <div className="staff-controls">
              <div className="control-group">
                <label>Staff Type</label>
                <select 
                  value={member.staff_type === 'revenue' ? 'revenue' : 'support'}
                  onChange={(e) => handleStaffTypeChange(member.id, e.target.value === 'revenue')}
                  className="control-select"
                >
                  <option value="revenue">Revenue</option>
                  <option value="support">Support</option>
                </select>
              </div>
              
              <div className="control-group">
                <label>Hourly Pay (£)</label>
                <input
                  type="number"
                  value={member.hourly_pay || 0}
                  onChange={(e) => handleStaffChange(member.id, 'hourly_pay', e.target.value)}
                  className="control-input"
                  step="0.1"
                  min="0"
                />
              </div>
              
              <div className="control-group">
                <label>Available Hours/Week</label>
                <input
                  type="number"
                  value={member.available_hours || 0}
                  onChange={(e) => handleStaffChange(member.id, 'available_hours', e.target.value)}
                  className="control-input"
                  step="0.1"
                  min="0"
                />
              </div>
              
              {member.staff_type === 'revenue' && (
                <div className="control-group">
                  <label>Booked Hours/Week</label>
                  <input
                    type="number"
                    value={member.booked_hours || 0}
                    onChange={(e) => handleStaffChange(member.id, 'booked_hours', e.target.value)}
                    className="control-input"
                    step="0.1"
                    min="0"
                    max={member.available_hours}
                  />
                </div>
              )}
            </div>
            
            {/* Card Actions */}
            <div className="card-actions">
              <button 
                className="btn-delete"
                onClick={() => handleDeleteStaff(member)}
                title="Remove from team"
              >
                🗑️ Remove
              </button>
            </div>
          </div>
        ))}
        
        {filteredStaff.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3>No {activeTab === 'all' ? 'team members' : `${activeTab} staff`} yet</h3>
            <p>Add your first team member to get started!</p>
          </div>
        )}
      </div>
      
      {/* Save Button */}
      {staff.length > 0 && (
        <div className="save-section">
          <button className="btn-save" onClick={handleSaveAllStaff}>
            💾 Save All Changes
          </button>
        </div>
      )}
      
      {/* Business Metrics */}
      {teamStats.revenueStaffCount > 0 && (
        <div className="business-metrics">
          <h3>📊 Business Performance</h3>
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">⏰</div>
              <div className="metric-content">
                <div className="metric-number">{teamStats.totalRevenueBookedHours.toFixed(1)}</div>
                <div className="metric-label">Weekly Revenue Hours</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">💰</div>
              <div className="metric-content">
                <div className="metric-number">£{teamStats.overheadPerHour.toFixed(0)}</div>
                <div className="metric-label">Required Revenue/Hour</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">🛠️</div>
              <div className="metric-content">
                <div className="metric-number">£{teamStats.supportStaffWeeklyCost.toFixed(0)}</div>
                <div className="metric-label">Support Staff/Week</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffCards;